import b from 'b_';
import { H2, Text, useIntl, useRouterPath, useStaticUrls } from 'platform-components';
import { HEADER_TEXT_SIZE } from 'platform-components/src/components/constants';
import { useWidthBreakpoints, WidthBreakpoints } from 'platform-components/src/utils/hooks/useWidthBreakpoints';
import qs from 'query-string';
import React, { memo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router';
import ym from 'react-yandex-metrika';

import { SEARCH_OPEN_MODULE, SEARCH_SCROLL_TO_LESSON } from 'common.components/constants';
import * as userActions from 'common.components/user/actions';
import { GO_TO_INFORMATICS_BANNER_CLICKED } from 'common.components/user/user.constants';
import { REDIRECT_SOURCE } from 'store/course-container/typings';

import { CourseContainerBannerButtons } from './__buttons/course-container-banner__buttons';
import { CourseContainerBannerCover } from './__cover/course-container-banner__cover';
import { CourseContainerBannerInfo } from './__info/course-container-banner__info';
import { CourseContainerBanner } from './course-container-banner';
import { Props } from './types';

import './course-container-banner.scss';
import { VIEW } from './constants';

const BREAKPOINTS: WidthBreakpoints<boolean> = [
    [true, 812],
    [false, 0],
];

// eslint-disable-next-line complexity
export const CourseContainerBannerContainer = memo(function CourseContainerBannerContainer({
    courseContainer,
    previewOnly,
}: Props) {
    const intl = useIntl();
    const location = useLocation();
    const { classId, subject } = useParams<{ classId?: string; subject: string }>();
    const dispatch = useDispatch();

    const ref = useRef<HTMLDivElement>(null);

    const withCover = useWidthBreakpoints(ref, BREAKPOINTS, false);
    const { slug } = courseContainer.container;
    const { lesson, module, group_name: groupName } = courseContainer.module_lesson_link;
    const containerHeader = intl.formatMessage({ id: `courseModuleBanner.${slug}.header` });

    const staticUrls = useStaticUrls();
    const path = previewOnly // eslint-disable-line no-nested-ternary
        ? classId
            ? staticUrls.labClassCoursesPreviewCourse
            : staticUrls.labPreviewCourse
        : staticUrls.labLibrarySubjectCollection;
    const informaticsLink = `${useRouterPath(path, { containerSlug: slug })}${location.search}`;
    const lastModuleLink = `${useRouterPath(path, { containerSlug: slug })}?${qs.stringify({
        [SEARCH_OPEN_MODULE]: module?.id || undefined,
        [SEARCH_SCROLL_TO_LESSON]: lesson?.id || undefined,
        ...qs.parse(location.search), // для мэш нужно query
    })}`;

    if (!(lesson && module)) {
        const handleClick = () => {
            dispatch(userActions.setWizardVariables({ [GO_TO_INFORMATICS_BANNER_CLICKED]: true }));
            ym('reachGoal', 'teacher_look_program', { from: REDIRECT_SOURCE.LESSON, cc_slug: slug });
        };

        const secondaryLink = intl.formatMessage({ id: `courseModuleBanner.button.secondary.default.${slug}.link` });

        // КК без выданного урока
        return (
            <CourseContainerBanner
                className={b('course-container-banner', { view: VIEW.DEFAULT })}
                courseContainer={courseContainer}
                ref={ref}
            >
                <div className="course-container-banner__content">
                    <H2 size={HEADER_TEXT_SIZE.S} weight={Text.WEIGHT.M}>
                        {containerHeader}
                    </H2>
                    <div className="course-container-banner__content-description">
                        <Text id={`courseModuleBanner.${slug}.description`} />
                    </div>
                    <CourseContainerBannerButtons
                        mainLink={informaticsLink}
                        onMainClick={handleClick}
                        secondaryLink={secondaryLink}
                        view={VIEW.DEFAULT}
                    />
                </div>
                <CourseContainerBannerInfo slug={slug} />
            </CourseContainerBanner>
        );
    }

    const handleMainClick = () => {
        dispatch(userActions.setWizardVariables({ lastContainerAssignmentBannerClicked: true }));

        ym('reachGoal', 'teacher_look_program', {
            from: REDIRECT_SOURCE.LESSON,
            cc_slug: slug,
            module_id: module.id,
            lesson_id: lesson.id,
        });
        ym('reachGoal', 'teacher_lesson_creation_opened_module', { subject, moduleId: module.id });
    };

    const handleSecondaryClick = () => {
        dispatch(userActions.setWizardVariables({ lastContainerAssignmentBannerClicked: true }));

        ym('reachGoal', 'teacher_lesson_creation_opened_module');
    };

    const shortContainerHeader = intl.formatMessage({ id: `courseModuleBanner.${slug}.shortHeader` });

    const continueAssignmentText = intl.formatMessage({ id: 'courseModuleBanner.continue' });

    return (
        <CourseContainerBanner
            className={b('course-container-banner', { view: VIEW.WITH_LESSON })}
            courseContainer={courseContainer}
            ref={ref}
        >
            {withCover && courseContainer.container.cover && (
                <CourseContainerBannerCover
                    backgroundColor={courseContainer.container.cover_background_color}
                    src={courseContainer?.module_lesson_link?.module?.cover || courseContainer.container.cover}
                />
            )}
            <div className="course-container-banner__content">
                <div className="course-container-banner__content-module">
                    <span className="course-container-banner__content-module-header">
                        <Text size={Text.SIZE.XL} type={Text.TYPE.HTML}>
                            {`${shortContainerHeader} • `}
                        </Text>
                    </span>
                    <span className="course-container-banner__content-module-name">
                        <Text size={Text.SIZE.XL} type={Text.TYPE.HTML}>
                            {module.name}
                        </Text>
                    </span>
                </div>
                <div className="course-container-banner__content-lesson">
                    {groupName && (
                        <Text
                            className="course-container-banner__content-lesson-group-name"
                            size={Text.SIZE.XL}
                            type={Text.TYPE.MARKDOWN}
                            weight={Text.WEIGHT.M}
                        >
                            {`${continueAssignmentText} ${groupName}`}
                        </Text>
                    )}
                    {lesson.name && (
                        <Text
                            className="course-container-banner__content-lesson-name"
                            size={Text.SIZE.XL}
                            type={Text.TYPE.MARKDOWN}
                        >
                            {lesson.name}
                        </Text>
                    )}
                </div>
                <CourseContainerBannerButtons
                    mainLink={lastModuleLink}
                    onMainClick={handleMainClick}
                    onSecondaryClick={handleSecondaryClick}
                    secondaryLink={informaticsLink}
                    view={VIEW.WITH_LESSON}
                />
            </div>
        </CourseContainerBanner>
    );
});
